/**
 * TanStack Query хуки задач.
 *
 * Mock-режим (по умолчанию): локальный jotai-атом c `MOCK_TASKS` для UI-демо.
 * Реальный режим: workspace-scoped эндпоинты `/workspaces/{ws}/tasks` colver;
 * имена менеджеров резолвятся по участникам воркспейса.
 */
import {useAuth} from '@api/auth';
import {isAxiosError} from 'axios';
import {MOCK_TODAY} from '@lib/utils';
import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import {atom, useAtom} from 'jotai';
import {useMemo} from 'react';
import {useRole} from '@hooks/useRole';
import {
  useActiveWorkspace,
  useWorkspaceMembers,
  usePersonalScope,
} from '@api/workspaces';
import {MOCK_TASKS} from './mock';
import {tasksClient} from './client';
import {
  adaptBackendTask,
  taskStateFromStatus,
  type BackendUpdateTaskInput,
  type TaskListInput,
  type TaskCreationContext,
  type UpdateTaskCommentInput,
} from './types';
import type {
  Task,
  TaskComment,
  CreateTaskInput,
  UpdateTaskInput,
} from './types';
import {IS_MOCK} from '../mock-mode';

const tasksAtom = atom<Task[]>(
  MOCK_TASKS.map((task) => ({
    ...task,
    creatorUserSid: task.creator === 'rop' ? 'mock-user-sid' : task.managerId,
    observerUserSid: task.withObserver ? 'mock-user-sid' : null,
  })),
);
/** Единый ID текущего участника задач: реальный пользователь либо выбранный demo-менеджер. */
export const useTaskActorSid = () => {
  const {currentUser} = useAuth();
  const role = useRole();
  return IS_MOCK && role === 'manager' ? 'emp-1' : currentUser?.sid;
};
const mockVisible = (task: Task, actorSid?: string) =>
  Boolean(
    actorSid &&
    [task.creatorUserSid, task.managerId, task.observerUserSid].includes(
      actorSid,
    ),
  );

/** Ошибки доступа не повторяются с устаревшими данными на экране. */
const taskReadRetry = (failureCount: number, error: unknown) => {
  if (
    isAxiosError(error) &&
    [400, 401, 403, 404, 409, 422].includes(error.response?.status ?? 0)
  )
    return false;
  return failureCount < 2;
};

/** Инвалидация после мутаций задач: сами задачи + очереди дашборда
 *  (закрытие задачи с communicationId архивирует элемент очереди на бэке). */
const invalidateTaskScopes = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({queryKey: ['tasks']});
  void qc.invalidateQueries({queryKey: ['queues']});
  void qc.invalidateQueries({queryKey: ['queue-resolutions']});
};

/** Резолвер имён участников воркспейса (real-режим). */
const useNameResolver = () => {
  const {activeId} = useActiveWorkspace();
  const {currentUser} = useAuth();
  const scope = usePersonalScope();
  const {data: members = []} = useWorkspaceMembers(
    IS_MOCK || !scope.isReady || scope.isPersonal ? null : activeId,
  );
  return useMemo(() => {
    const map = new Map(members.map((m) => [m.userSid, m]));
    return (userSid: string | null | undefined): string | null => {
      if (!userSid) return null;
      if (userSid === currentUser?.sid)
        return currentUser.displayName || currentUser.email || userSid;
      const m = map.get(userSid);
      return m?.displayName || m?.email || userSid.slice(0, 8);
    };
  }, [members, currentUser]);
};

/** Страницы доступных задач: следующая порция загружается только по действию пользователя. */
export const useTasks = ({
  filter = {},
  enabled = true,
}: {
  filter?: TaskListInput['filter'];
  enabled?: boolean;
} = {}) => {
  const [mockTasks] = useAtom(tasksAtom);
  const {activeId} = useActiveWorkspace();
  const actorSid = useTaskActorSid();
  const resolveName = useNameResolver();
  const q = useInfiniteQuery({
    retry: taskReadRetry,
    queryKey: [
      'tasks',
      IS_MOCK ? 'mock' : actorSid,
      activeId,
      'pages',
      actorSid,
      filter,
    ],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({pageParam}) => {
      const page = await tasksClient.list(activeId as string, {
        filter,
        limit: 50,
        cursor: pageParam,
      });
      return {
        ...page,
        rows: page.rows.map((task) => adaptBackendTask(task, resolveName)),
      };
    },
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !IS_MOCK && enabled && Boolean(activeId && actorSid),
  });
  const data = useMemo(
    () =>
      IS_MOCK
        ? mockTasks.filter(
            (task) =>
              mockVisible(task, actorSid) &&
              (!filter.managerUserSid ||
                task.managerId === filter.managerUserSid) &&
              (!filter.communicationIds ||
                (task.communicationId !== null &&
                  filter.communicationIds.includes(task.communicationId))) &&
              (!filter.states ||
                filter.states.includes(taskStateFromStatus(task.status))),
          )
        : q.isError
          ? []
          : (q.data?.pages.flatMap((page) => page.rows) ?? []),
    [mockTasks, actorSid, filter, q.data, q.isError],
  );
  return {
    ...q,
    data,
    isSuccess: IS_MOCK || q.isSuccess,
    isLoading: !IS_MOCK && q.isLoading,
    hasNextPage: !IS_MOCK && q.hasNextPage,
  };
};

/** Разрешённые исполнитель и наблюдатель из общей политики сервера. */
export const useTaskCreationContext = (enabled = true) => {
  const {activeId} = useActiveWorkspace();
  const {currentUser} = useAuth();
  const actorSid = useTaskActorSid();
  const role = useRole();
  const [mockTasks] = useAtom(tasksAtom);
  return useQuery({
    queryKey: [
      'tasks',
      actorSid,
      activeId,
      'creation-context',
      IS_MOCK ? role : null,
    ],
    queryFn: async (): Promise<TaskCreationContext> => {
      if (IS_MOCK) {
        const self = {
          userSid: actorSid!,
          displayName:
            role === 'manager'
              ? 'Алексей К.'
              : currentUser?.displayName || 'РОП',
        };
        const team = [
          ...new Map(
            mockTasks
              .filter((task) => task.managerId)
              .map((task) => [
                task.managerId!,
                {
                  userSid: task.managerId!,
                  displayName: task.manager || task.managerId!,
                },
              ]),
          ).values(),
        ];
        return {
          assignees: role === 'rop' ? [self, ...team] : [self],
          observer:
            role === 'manager'
              ? {userSid: 'mock-user-sid', displayName: 'Алексей П. (РОП)'}
              : null,
        };
      }
      const page = await tasksClient.list(activeId!, {
        limit: 1,
        include: ['creation-context'],
      });
      if (!page.creationContext)
        throw new Error('Контекст создания задачи недоступен');
      return page.creationContext;
    },
    enabled: enabled && Boolean(actorSid) && (IS_MOCK || Boolean(activeId)),
    staleTime: 0,
  });
};

/** Сводки задач звонков одним ограниченным batch на порцию до 100 источников. */
export const useTaskSourceSummaries = (communicationIds: string[]) => {
  const {activeId} = useActiveWorkspace();
  const actorSid = useTaskActorSid();
  const resolveName = useNameResolver();
  const [mockTasks] = useAtom(tasksAtom);
  const ids = [...new Set(communicationIds.filter(Boolean))].sort();
  const q = useQuery({
    retry: taskReadRetry,
    queryKey: ['tasks', actorSid, activeId, 'source-summary', ids],
    queryFn: async () => {
      const result: Record<string, {count: number; tasks: Task[]}> = {};
      // Очередь может содержать больше ста звонков; не создаём N+1 на каждую строку.
      for (let offset = 0; offset < ids.length; offset += 100) {
        const input: TaskListInput = {
          filter: {communicationIds: ids.slice(offset, offset + 100)},
          limit: 1,
          include: ['source-summary'],
        };
        const page = await tasksClient.list(activeId!, input);
        for (const item of page.sourceSummaries ?? [])
          result[item.communicationId] = {
            count: item.count,
            tasks: item.tasks.map((task) =>
              adaptBackendTask(task, resolveName),
            ),
          };
      }
      return result;
    },
    enabled: !IS_MOCK && Boolean(activeId && actorSid) && ids.length > 0,
  });
  const mock = useMemo(() => {
    const result: Record<string, {count: number; tasks: Task[]}> = {};
    for (const task of mockTasks)
      if (
        task.communicationId &&
        ids.includes(task.communicationId) &&
        mockVisible(task, actorSid)
      ) {
        const item = (result[task.communicationId] ??= {count: 0, tasks: []});
        item.count++;
        if (item.tasks.length < 5) item.tasks.push(task);
      }
    return result;
  }, [mockTasks, actorSid, communicationIds]);
  return {...q, data: IS_MOCK ? mock : q.isError ? {} : (q.data ?? {})};
};

/** Деталь задачи независимо от ограниченной ленты и текущих фильтров страницы. */
export const useTask = (sid: string | null, enabled = true) => {
  const {activeId} = useActiveWorkspace();
  const {currentUser} = useAuth();
  const [mockTasks] = useAtom(tasksAtom);
  const actorSid = useTaskActorSid();
  const resolveName = useNameResolver();
  const mockTask = useMemo(() => {
    const task = mockTasks.find((item) => item.id === sid);
    if (!task || !mockVisible(task, actorSid)) return undefined;
    return {
      ...task,
      comments: task.comments.map((comment) => ({
        ...comment,
        canEdit: Boolean(actorSid && comment.authorUserSid === actorSid),
        canDelete: Boolean(actorSid && comment.authorUserSid === actorSid),
      })),
      mentionCandidates: [
        ...new Set([task.creatorUserSid, task.managerId, task.observerUserSid]),
      ]
        .filter((userSid): userSid is string =>
          Boolean(userSid && userSid !== actorSid),
        )
        .map((userSid) => ({
          userSid,
          displayName:
            userSid === 'mock-user-sid'
              ? 'Алексей П. (РОП)'
              : task.manager || userSid,
        })),
    };
  }, [mockTasks, sid, actorSid]);
  const query = useQuery({
    retry: taskReadRetry,
    queryKey: ['tasks', currentUser?.sid, activeId, 'detail', sid],
    queryFn: async () =>
      adaptBackendTask(
        await tasksClient.get(activeId as string, sid as string),
        resolveName,
      ),
    enabled:
      !IS_MOCK && enabled && Boolean(sid && activeId && currentUser?.sid),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  return {
    ...query,
    data: IS_MOCK ? mockTask : query.data,
    isLoading: !IS_MOCK && query.isLoading,
    isError: IS_MOCK ? Boolean(enabled && sid && !mockTask) : query.isError,
  };
};

export const useCreateTask = () => {
  const actorRole = useRole();
  const [, setTasks] = useAtom(tasksAtom);
  const {activeId} = useActiveWorkspace();
  const actorSid = useTaskActorSid();
  const resolveName = useNameResolver();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: CreateTaskInput & {
        creator: 'rop' | 'manager';
        managerName?: string;
      },
    ): Promise<Task> => {
      if (IS_MOCK) {
        const newTask: Task = {
          id: `T-${Date.now()}`,
          title: input.title,
          text: input.text ?? null,
          managerId: input.managerId ?? null,
          manager: input.managerName ?? null,
          priority: input.priority,
          status: input.status ?? 'planned',
          communicationId: input.communicationId ?? null,
          dueDate: input.dueDate,
          createdAt: `${MOCK_TODAY}T12:00:00Z`,
          creator: actorRole === 'rop' ? 'rop' : 'manager',
          creatorUserSid: actorSid ?? null,
          withObserver: Boolean(input.observerUserSid),
          observerUserSid: input.observerUserSid ?? null,
          comments: [],
        };
        setTasks((prev) => [newTask, ...prev]);
        return newTask;
      }
      const raw = await tasksClient.create(
        activeId as string,
        {
          title: input.title,
          text: input.text,
          managerUserSid: input.managerId,
          observerUserSid: input.observerUserSid,
          communicationId: input.communicationId,
          priority: input.priority,
          state: taskStateFromStatus(input.status ?? 'planned'),
          dueDate: input.dueDate || null,
        },
        input.idempotencyKey,
      );
      return adaptBackendTask(raw, resolveName);
    },
    onSuccess: () => invalidateTaskScopes(qc),
  });
};

/** Маппинг фронтового UpdateTaskInput в PATCH-тело бэка (без comments/manager-имени). */
const toBackendUpdate = (input: UpdateTaskInput): BackendUpdateTaskInput => {
  const out: BackendUpdateTaskInput = {};
  if (input.title !== undefined) out.title = input.title;
  if (input.text !== undefined) out.text = input.text ?? null;
  if (input.priority !== undefined) out.priority = input.priority;
  if (input.status !== undefined) out.state = taskStateFromStatus(input.status);
  if (input.dueDate !== undefined) out.dueDate = input.dueDate || null;
  if (input.managerId !== undefined) out.managerUserSid = input.managerId;
  if (input.communicationId !== undefined)
    out.communicationId = input.communicationId;
  return out;
};

export const useUpdateTask = () => {
  const [, setTasks] = useAtom(tasksAtom);
  const {activeId} = useActiveWorkspace();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({id, input}: {id: string; input: UpdateTaskInput}) => {
      if (IS_MOCK) {
        setTasks((prev) =>
          prev.map((t) => (t.id === id ? {...t, ...input} : t)),
        );
        return;
      }
      await tasksClient.patch(activeId as string, id, toBackendUpdate(input));
    },
    onSuccess: () => invalidateTaskScopes(qc),
  });
};

export const useDeleteTask = () => {
  const [, setTasks] = useAtom(tasksAtom);
  const {activeId} = useActiveWorkspace();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (IS_MOCK) {
        setTasks((prev) => prev.filter((t) => t.id !== id));
        return;
      }
      await tasksClient.remove(activeId as string, id);
    },
    onSuccess: () => invalidateTaskScopes(qc),
  });
};

/** Добавить комментарий к задаче (real — отдельный endpoint; mock — атом). */
export const useAddTaskComment = () => {
  const [tasks, setTasks] = useAtom(tasksAtom);
  const actorSid = useTaskActorSid();
  const {activeId} = useActiveWorkspace();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      text,
      mentions = [],
      author,
      idempotencyKey,
    }: {
      taskId: string;
      text: string;
      mentions?: string[];
      idempotencyKey: string;
      /** Подпись автора для mock-режима. */
      author?: string;
    }) => {
      if (IS_MOCK) {
        const task = tasks.find((item) => item.id === taskId);
        if (!task || !mockVisible(task, actorSid))
          throw new Error('Задача недоступна');
        const participants = [
          task.creatorUserSid,
          task.managerId,
          task.observerUserSid,
        ];
        if (
          mentions.some(
            (sid) => sid === actorSid || !participants.includes(sid),
          )
        )
          throw new Error('Недопустимый участник');
        const previous = task.comments.find(
          (item) => item.id === `C-${idempotencyKey}`,
        );
        if (previous) {
          if (
            previous.text !== text ||
            JSON.stringify(previous.mentions) !== JSON.stringify(mentions)
          )
            throw new Error('Ключ уже использован');
          return;
        }
        const comment: TaskComment = {
          id: `C-${idempotencyKey}`,
          author: author ?? 'РОП',
          authorUserSid: actorSid,
          updatedAt: null,
          text,
          mentions,
          mentionUsers: mentions.map((userSid) => ({
            userSid,
            displayName:
              userSid === 'mock-user-sid'
                ? 'Алексей П. (РОП)'
                : task.manager || userSid,
          })),
          createdAt: `${MOCK_TODAY}T12:00:00Z`,
        };
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId ? {...t, comments: [...t.comments, comment]} : t,
          ),
        );
        return;
      }
      await tasksClient.addComment(
        activeId as string,
        taskId,
        {
          text,
          mentions,
        },
        idempotencyKey,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: ['tasks']});
      void qc.invalidateQueries({queryKey: ['notifications']});
    },
  });
};

/** Изменение/удаление комментариев с обновлением actor-scoped detail и уведомлений. */
const useTaskCommentMutation = (operation: 'edit' | 'delete') => {
  const [tasks, setTasks] = useAtom(tasksAtom);
  const actorSid = useTaskActorSid();
  const {activeId} = useActiveWorkspace();
  const resolveName = useNameResolver();
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    onMutate: async ({
      taskId,
    }: {
      taskId: string;
      commentId: string;
      input?: UpdateTaskCommentInput;
    }) => {
      const detailKey = ['tasks', actorSid, activeId, 'detail', taskId];
      await qc.cancelQueries({queryKey: detailKey});
      return {detailKey};
    },
    mutationFn: async ({
      taskId,
      commentId,
      input,
    }: {
      taskId: string;
      commentId: string;
      input?: UpdateTaskCommentInput;
    }) => {
      if (IS_MOCK) {
        const task = tasks.find((item) => item.id === taskId);
        const comment = task?.comments.find((item) => item.id === commentId);
        if (!task || !mockVisible(task, actorSid) || !comment)
          throw new Error('Комментарий недоступен');
        if (comment.authorUserSid !== actorSid)
          throw new Error('Можно изменять только свои комментарии');
        if (operation === 'edit') {
          if (!input?.text.trim() || input.text.length > 5000)
            throw new Error('Комментарий — от 1 до 5000 символов');
          const participants = [
            task.creatorUserSid,
            task.managerId,
            task.observerUserSid,
          ];
          if (
            input.mentions &&
            (new Set(input.mentions).size > 2 ||
              input.mentions.some(
                (sid) => sid === actorSid || !participants.includes(sid),
              ))
          )
            throw new Error('Недопустимые упоминания');
        }
        setTasks((prev) =>
          prev.map((item) =>
            item.id !== taskId
              ? item
              : {
                  ...item,
                  comments:
                    operation === 'delete'
                      ? item.comments.filter((c) => c.id !== commentId)
                      : item.comments.map((c) =>
                          c.id !== commentId
                            ? c
                            : {
                                ...c,
                                text: input!.text,
                                mentions: input!.mentions ?? c.mentions,
                                mentionUsers: (
                                  input!.mentions ?? c.mentions
                                ).map((userSid) => ({
                                  userSid,
                                  displayName:
                                    c.mentionUsers?.find(
                                      (person) => person.userSid === userSid,
                                    )?.displayName ??
                                    (userSid === 'mock-user-sid'
                                      ? 'Алексей П. (РОП)'
                                      : task.manager || userSid),
                                })),
                                updatedAt: `${MOCK_TODAY}T12:01:00Z`,
                              },
                        ),
                },
          ),
        );
        return;
      }
      if (!activeId || !actorSid) throw new Error('Задача недоступна');
      if (operation === 'delete') {
        await tasksClient.deleteComment(activeId, taskId, commentId);
        return;
      }
      if (!input) throw new Error('Не задан текст комментария');
      return adaptBackendTask(
        await tasksClient.updateComment(activeId, taskId, commentId, input),
        resolveName,
      );
    },
    onSuccess: async (task, {commentId}, context) => {
      if (!IS_MOCK && context) {
        await qc.cancelQueries({queryKey: context.detailKey});
        if (task) qc.setQueryData(context.detailKey, task);
        else
          qc.setQueryData<Task>(
            context.detailKey,
            (previous) =>
              previous && {
                ...previous,
                comments: previous.comments.filter(
                  (comment) => comment.id !== commentId,
                ),
              },
          );
      }
    },
    // Ошибка тоже требует перепроверить доступ/данные: запрос мог выполниться до обрыва связи.
    onSettled: () => {
      void qc.invalidateQueries({queryKey: ['tasks']});
      void qc.invalidateQueries({queryKey: ['notifications']});
    },
  });
};

/** Редактирование своего комментария, независимо от состояния задачи. */
export const useUpdateTaskComment = () => useTaskCommentMutation('edit');

/** Удаление своего комментария без изменения остальных полей задачи. */
export const useDeleteTaskComment = () => useTaskCommentMutation('delete');
